import { useRef } from 'react';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import { useDebounceCallback } from '@/hooks/state/use-debounce-callback';
import { SEEK_DEBOUNCE_MS } from '../constants';
import * as styles from './wistia-embed.css';

interface WistiaEmbedProps {
  id: string;
  onStart?: () => void;
  onEnd?: () => void;
  onPause?: (pauseTime: number) => void;
  onSeek?: (toTime: number, fromTime: number) => void;
  className?: string;
}

export function WistiaEmbed({ id, onStart, onEnd, onPause, onSeek, className }: WistiaEmbedProps) {
  const seekFromTimeRef = useRef<{ id: string; time: number } | null>(null);

  const handlePlay = () => {
    onStart?.();
  };

  const handleEnded = () => {
    onEnd?.();
  };

  const handlePause = (event: CustomEvent) => {
    const currentTime = event.detail?.currentTime ?? 0;
    onPause?.(currentTime);
  };

  const debouncedSeek = useDebounceCallback((toTime: number, fromTime: number) => {
    onSeek?.(Number(toTime.toFixed(2)), Number(fromTime.toFixed(2)));
    seekFromTimeRef.current = null;
  }, SEEK_DEBOUNCE_MS);

  const handleSeek = (event: CustomEvent) => {
    const toTime = event.detail?.currentTime ?? 0;
    const fromTime = event.detail?.lastTime ?? 0;

    const seekFrom = seekFromTimeRef.current?.id === id ? seekFromTimeRef.current.time : fromTime;
    seekFromTimeRef.current = { id, time: seekFrom };
    debouncedSeek(toTime, seekFrom);
  };

  return (
    <div className={className}>
      <WistiaPlayer
        mediaId={id}
        onPlay={handlePlay}
        onEnded={handleEnded}
        onPause={handlePause}
        onSeeked={handleSeek}
        className={styles.playerContainer}
      />
    </div>
  );
}
